import type { ChannelSlice } from '../ChannelSlice';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_MENU_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { effect, signal, computed, batch, untracked } from '@preact/signals-react';
import type { Signal, ReadonlySignal } from '@preact/signals-react';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import type { IndicatorImportRef } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { IdeaRecord, WatchlistPlan } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import type { Universe } from '@thinkalpha/platform-ws-client/contracts/universe.js';
import type { ConcreteAccount } from '@thinkalpha/trading-ws-client/contracts/account.js';
import type { Order } from '@thinkalpha/trading-ws-client/contracts/order.js';
import { optional } from 'inversify';
import { generateAccountNameToDisplay } from 'src/components/menus/AccountsMenu/utils';
import { ReactiveInjectable, reacts, inject, injectable } from 'src/features/ioc';
import { ensureColumns, ensureColumnBySlugWithName } from 'src/features/table/lib/ensureColumns';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { instrumentToString, instrumentWithStringExpiryToString, parseInstrument } from 'src/lib/instrumentUtils';
import { createTable } from 'src/lib/table';
import { SourceStage, FilterStage, IdeaStage, OutputStage } from 'src/lib/tableStages';
import { BASKET_TRADING_PREFERRED_POSITION_ADJUSTMENT_ACCOUNT } from 'src/lib/trading/preferredAccounts/config';
import { getPreferredAccountId } from 'src/lib/trading/preferredAccounts/getPreferredAccountId';
import type { BasketTradingWidgetModel } from 'src/models/BasketTradingWidgetModel';
import {
    invalidateBasketSpecsQueries,
    createBasketSpec,
    updateBasketSpec,
    getBasketSpecById,
} from 'src/queries/basketSpecs';
import { getIdeaByIdQuery, getIdeaRecordByIdQuery } from 'src/queries/ideas';
import { getUniverseByIdQuery } from 'src/queries/universes';
import type { NewBasketOrderSchemaType } from 'src/routes/widgets/BasketTradingWidget/schema';
import {
    setBasketLabel,
    removeOrder,
    initializeState,
    basketTradingReducer,
    initialState,
    addOrder,
    addOrdersBatch,
    resetBasket,
    updateOrder,
    setBasketType,
    redistributeWeights,
    setWeightBasis,
    setNotionalAmount,
    setNotionalType,
    isWeightSumValid,
    setDefaults,
    setPositionsAccount,
} from 'src/routes/widgets/BasketTradingWidget/state';
import type {
    BasketTradingState,
    BasketTradingAction,
    BasketOrder,
} from 'src/routes/widgets/BasketTradingWidget/state';
import {
    basketStateToSpec,
    serializableBasketSnapshot,
} from 'src/routes/widgets/BasketTradingWidget/state/basket-spec';
import type { BasketSpec } from 'src/routes/widgets/BasketTradingWidget/state/basket-spec';
import { calculateNotionalQuantity } from 'src/routes/widgets/BasketTradingWidget/state/helpers';
import type { SendOrderData } from 'src/routes/widgets/TradeProWidget/api';
import type { AccountService } from 'src/services/AccountService';
import { AccountServiceImpl } from 'src/services/AccountService/impl';
import type { MarketDataSubscription } from 'src/services/MarketDataService';
import type { PositionInfoSubscription } from 'src/services/PositionInfoGroupService';
import type { ToastShowOptions } from 'src/services/ToastService';
import type { TradingSocketConnectionStatus, TradingSocketService } from 'src/services/TradingSocketService';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import { container } from 'src/StaticContainer';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';

interface PositionInfo {
    symbol: string;
    quantity: number;
}

const log = container.get('Logger').getSubLogger({ name: 'BasketTradingWidgetModelImpl' }, { component: 'trading' });

const LOAD_BASKET_SYMBOL_LABEL = 'Load Symbol List';

@injectable()
export class BasketTradingWidgetModelImpl extends ReactiveInjectable implements BasketTradingWidgetModel {
    #state: Signal<BasketTradingState> = signal(structuredClone(initialState));
    #devtools: ReducerDevtoolsConnection<BasketTradingState, BasketTradingAction> | undefined;

    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;

    #accountService: AccountService;

    #marketDataUpdates: Record<string, MarketDataSubscription | undefined> = {};
    #positionInfoUpdates = signal<Record<string, PositionInfoSubscription | undefined>>({});

    // Two separate MarketDataGroupService instances for Options and Equities
    #optionsMarketDataGroup: ReactBindings['MarketDataGroupService'];
    #equityMarketDataGroup: ReactBindings['MarketDataGroupService'];

    // PositionInfoGroupService for real-time position updates
    #positionInfoGroup: ReactBindings['PositionInfoGroupService'] | null = null;
    #positionInfoGroupReady: Promise<void> | null = null;

    #t: Awaited<ReturnType<ReactBindings['TProvider']>> | null = null;

    #accountId = computed(() => this.#state.value.defaults.account);

    // Save/load state
    #loadedBasketId = signal<string | null>(null);
    #basketName = signal<string>('');
    #lastSavedName = signal<string>('');
    #viewMode = signal<'basket' | 'list'>('basket');
    #lastSavedStateJson = signal<string>('');

    readonly #isDirty = computed(() => {
        // A new basket with no orders and no name is never dirty — auto-filled
        // defaults (account, positionsAccount) should not trigger the dirty flag.
        if (!this.#loadedBasketId.value && this.#state.value.basket.length === 0 && !this.#basketName.value) {
            return false;
        }
        return (
            serializableBasketSnapshot(this.#state.value) !== this.#lastSavedStateJson.value ||
            this.#basketName.value !== this.#lastSavedName.value
        );
    });
    readonly #canSave = computed(() => this.#isDirty.value);
    readonly #selectedBasket = computed(() => {
        const id = this.#loadedBasketId.value;
        return id ? { id } : null;
    });

    get basketName() {
        return this.#basketName as ReadonlySignal<string>;
    }

    get viewMode() {
        return this.#viewMode as ReadonlySignal<'basket' | 'list'>;
    }

    headerElements: WidgetHeaderElement[] = [
        ...COMMON_MENU_HEADER_ELEMENTS,
        { type: 'connectionStatus', position: 995, priority: 995 },
        { type: 'basketTradingName', position: 20, priority: 5 },
        { type: 'basketTradingSave', position: 25, priority: 5 },
        {
            type: 'basketTradingMenuItems',
            position: 2,
            overflowOnly: true,
            items: ['new', 'open', 'save', 'copy', 'delete', 'allBaskets'],
        },
    ];

    eventBus!: BasketTradingWidgetModel['eventBus'];

    #buildEventBus(): BasketTradingWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Connection status signal
                .addSignal('widget::connectionStatus', () =>
                    computed(() => ({
                        status: this.#tradingSocketConnectionStatus.value,
                        progress: undefined,
                        failureCount: 0,
                    })),
                )
                // Basket save/load signals
                .addSignal('basketTrading::name', () => this.#basketName)
                .addSignal('basketTrading::editorIsDirty', () => this.#isDirty)
                .addSignal('basketTrading::editorCanSave', () => this.#canSave)
                .addSignal('basketTrading::selectedBasket', () => this.#selectedBasket)
                // viewMode signal kept for future "Open Baskets" (execution results) feature
                .addSignal('basketTrading::viewMode', () => this.#viewMode)
                // Basket save/load events
                .addEvent('basketTrading::setName', ({ name }) => {
                    this.#basketName.value = name;
                })
                .addEvent('basketTrading::editorSave', () => {
                    void this.#saveBasket();
                })
                .addEvent('basketTrading::editorSaveAs', ({ name }) => {
                    void this.#saveBasketAs(name);
                })
                .addEvent('basketTrading::editorCreateNew', () => {
                    this.#createNewBasket();
                    this.#viewMode.value = 'basket';
                })
                .addEvent('basketTrading::editorLoad', ({ specId }) => {
                    this.#viewMode.value = 'basket';
                    void this.#loadBasketById(specId);
                })
                .addEvent('basketTrading::setViewMode', ({ mode }) => {
                    this.#viewMode.value = mode;
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus) as BasketTradingWidgetModel['eventBus'];
    }

    constructor(
        @inject('WidgetDataModel') @reacts private widgetData: ReactBindings['WidgetDataModel'],
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('MarketDataGroupService') optionsMarketDataGroup: ReactBindings['MarketDataGroupService'],
        @inject('MarketDataGroupService') equityMarketDataGroup: ReactBindings['MarketDataGroupService'],
        @inject('PositionInfoGroupServiceProvider')
        private positionInfoGroupServiceProvider: ReactBindings['PositionInfoGroupServiceProvider'],
        @inject('TableModelProvider') private tableModelProvider: ReactBindings['TableModelProvider'],
        @inject('FormulaService') private formulaService: ReactBindings['FormulaService'],
        @inject('SlugMapServiceProvider')
        private readonly slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('SlugMapService') private readonly slugMapService: ReactBindings['SlugMapService'],
        @inject('TProvider') private t: ReactBindings['TProvider'],
        @inject('ToastService') private readonly toasts: ReactBindings['ToastService'],
        @inject('SuggestionsService') private suggestionsService: ReactBindings['SuggestionsService'],
        @inject('TradingSocketService') private tradingSocketService: TradingSocketService,
        @inject('CurrentUserModel') private currentUserModel: ReactBindings['CurrentUserModel'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }
        this.eventBus = this.#buildEventBus();
        this.#accountService = new AccountServiceImpl(queryClient);

        // Store the two MarketDataGroupService instances
        this.#optionsMarketDataGroup = optionsMarketDataGroup;
        this.#optionsMarketDataGroup.setDevtoolsName('BasketTrading/optionsMarketData');
        this.#equityMarketDataGroup = equityMarketDataGroup;
        this.#equityMarketDataGroup.setDevtoolsName('BasketTrading/equityMarketData');

        // Ensure cleanup of MarketDataGroupService instances
        this.disposableStack.defer(() => {
            this.#optionsMarketDataGroup[Symbol.dispose]();
            this.#equityMarketDataGroup[Symbol.dispose]();
        });

        // Calculate quantities for notional basket orders based on notionalAmount, notionalType, and market prices
        this.disposableStack.defer(
            effect(() => {
                // Only run this effect for notional basket type
                if (this.#state.value.type !== 'notional') {
                    return;
                }

                const basket = this.#state.value.basket;
                const notionalAmount = this.#state.value.notionalAmount;
                const notionalType = this.#state.value.notionalType;

                if (basket.length === 0) {
                    return;
                }

                // If there's no notionalAmount, set all quantities to 0
                if (!notionalAmount) {
                    for (const order of basket) {
                        if (order.quantity !== 0) {
                            this.dispatch(
                                updateOrder({
                                    ...order,
                                    quantity: 0,
                                }),
                            );

                            if (TRACE) {
                                log.trace('Reset order quantity to 0 (no notional amount)', {
                                    instrument: order.instrument,
                                    oldQuantity: order.quantity,
                                });
                            }
                        }
                    }
                    return;
                }

                if (TRACE) {
                    log.trace('Recalculating notional basket quantities', {
                        notionalAmount,
                        notionalType,
                        basketSize: basket.length,
                    });
                }

                // Recalculate quantity for each order based on current prices
                for (const order of basket) {
                    // Access signals to ensure reactivity
                    const bidPrice = order.bidPrice?.value;
                    const askPrice = order.askPrice?.value;

                    // Calculate the new quantity
                    const newQuantity = calculateNotionalQuantity(order, notionalAmount, notionalType);

                    // Only update if quantity changed to avoid unnecessary updates
                    if (order.quantity !== newQuantity) {
                        this.dispatch(
                            updateOrder({
                                ...order,
                                quantity: newQuantity,
                            }),
                        );

                        if (TRACE) {
                            log.trace('Updated order quantity', {
                                instrument: order.instrument,
                                oldQuantity: order.quantity,
                                newQuantity,
                                weight: order.weight,
                                side: order.side,
                                bidPrice,
                                askPrice,
                            });
                        }
                    }
                }
            }),
        );

        this.disposableStack.defer(
            effect(() => {
                const account = this.#accountId.value;

                if (!account) {
                    if (this.#positionInfoGroup) {
                        this.#positionInfoGroup[Symbol.dispose]();
                        this.#positionInfoGroup = null;
                        this.#positionInfoGroupReady = null;

                        for (const instrumentString in this.#positionInfoUpdates.value) {
                            if (this.#positionInfoUpdates.value[instrumentString]) {
                                this.#positionInfoUpdates.value[instrumentString][Symbol.dispose]();
                            }
                        }
                        this.#positionInfoUpdates.value = {};
                    }
                    return;
                }

                if (TRACE) {
                    log.trace('Account changed, creating PositionInfoGroupService for account', { account });
                }

                if (this.#positionInfoGroup) {
                    this.#positionInfoGroup[Symbol.dispose]();
                    this.#positionInfoGroup = null;
                    this.#positionInfoGroupReady = null;

                    for (const instrumentString in this.#positionInfoUpdates.value) {
                        if (this.#positionInfoUpdates.value[instrumentString]) {
                            this.#positionInfoUpdates.value[instrumentString][Symbol.dispose]();
                        }
                    }
                    this.#positionInfoUpdates.value = {};
                }

                this.#positionInfoGroupReady = untracked(async () => {
                    try {
                        if (TRACE) {
                            log.trace('Creating PositionInfoGroupService for account', { account });
                        }
                        this.#positionInfoGroup = await this.positionInfoGroupServiceProvider([account]);

                        if (TRACE) {
                            log.trace('PositionInfoGroupService created and ready', { account });
                        }

                        const basket = untracked(() => this.#state.value.basket);
                        const updates = { ...this.#positionInfoUpdates.value };

                        for (const order of basket) {
                            const instrumentString = instrumentToString(order.instrument);
                            const existingSubscription = updates[instrumentString];

                            if (!existingSubscription && this.#positionInfoGroup) {
                                log.debug('Creating position subscription for instrument', { instrumentString });
                                const subscription = await this.#fetchPositionInfoForSymbol(order.instrument);
                                if (subscription) {
                                    updates[instrumentString] = subscription;
                                }
                            }
                        }

                        this.#positionInfoUpdates.value = updates;
                    } catch (error) {
                        log.error('Failed to create PositionInfoGroupService', { error, account });
                    }
                });
            }),
        );

        // Subscribe to trading socket
        this.disposableStack.defer(
            effect(() => {
                const accountId = this.#accountId.value;

                if (accountId) {
                    return this.tradingSocketService.subscribeToAccounts([accountId]);
                }
            }),
        );

        // Select a preferred or first available positions account if none is explicitly set
        this.disposableStack.defer(
            effect(() => {
                const accounts = this.#accounts.value;
                const positionsAccount = this.#state.value.positionsAccount;

                if (accounts.length > 0 && !positionsAccount) {
                    const accountId = getPreferredAccountId(
                        this.currentUserModel.userId.value,
                        accounts,
                        BASKET_TRADING_PREFERRED_POSITION_ADJUSTMENT_ACCOUNT,
                    );
                    if (accountId) {
                        this.dispatch(setPositionsAccount(accountId));
                        if (TRACE) {
                            log.trace('Selected positions account', { account: accountId });
                        }
                    }
                }
            }),
        );
    }

    async init(tabId: string) {
        this.widgetData.init(tabId);

        // Initialize slices
        this.#channelSlice.init(tabId);
        this.#widgetTabSlice.init(tabId);
        this.disposableStack.use(this.#channelSlice);
        this.disposableStack.use(this.#widgetTabSlice);

        this.dispatch(initializeState());

        this.#devtools = connectReducerDevtools({ name: `BasketTrading [${tabId}]` });
        if (this.#devtools) {
            this.disposableStack.use(this.#devtools);
            this.#devtools.init(this.#state.value);
        }

        this.#loadAccounts();

        this.#t = await this.t();

        // Load recently accessed items
        void this.#updateRecentlyAccessed();

        // Initialize saved state snapshot for dirty tracking
        this.#lastSavedStateJson.value = serializableBasketSnapshot(this.#state.value);
        this.#lastSavedName.value = this.#basketName.value;
    }

    #isReady = computed(() => {
        const accountId = this.#accountId.value;
        return (
            this.tradingSocketService.isConnected.value &&
            this.tradingSocketService.isAuthenticated.value &&
            (accountId ? this.tradingSocketService.joinedAccounts.value.has(accountId) : false)
        );
    });
    get isReady(): ReadonlySignal<boolean> {
        return this.#isReady;
    }

    #tradingSocketConnectionStatus = computed(() => {
        return this.tradingSocketService.isConnected.value && this.tradingSocketService.isAuthenticated.value
            ? 'connected'
            : this.tradingSocketService.isConnected.value
              ? 'connecting'
              : 'disconnected';
    });
    get tradingSocketConnectionStatus(): ReadonlySignal<TradingSocketConnectionStatus> {
        return this.#tradingSocketConnectionStatus;
    }

    #enqueueSnackbar(
        message: Parameters<Awaited<ReturnType<ReactBindings['TProvider']>>>[0],
        options: Pick<ToastShowOptions, 'variant'>,
    ) {
        if (this.#t) {
            this.toasts.show({ message: this.#t(message) as string, ...options });
        }
    }

    dispatch(action: BasketTradingAction) {
        const currentState = this.#state.value;
        this.#state.value = basketTradingReducer(currentState, action);
        this.#devtools?.send(action, this.#state.peek());
    }

    #basketType: ReadonlySignal<BasketTradingState['type']> = computed(() => this.#state.value.type);
    get basketType() {
        return this.#basketType;
    }

    setBasketType(type: BasketTradingState['type']) {
        if (this.#state.value.type === 'positionAdjustment' && type !== 'positionAdjustment') {
            this.#isLoading.value = false;
        }
        this.dispatch(setBasketType(type));
    }

    #defaults: ReadonlySignal<BasketTradingState['defaults']> = computed(() => this.#state.value.defaults);
    get defaults() {
        return this.#defaults;
    }

    setDefaults(defaults: BasketTradingState['defaults']) {
        this.dispatch(setDefaults(defaults));
    }

    #weightBasis: ReadonlySignal<BasketTradingState['weightBasis']> = computed(() => this.#state.value.weightBasis);
    get weightBasis() {
        return this.#weightBasis;
    }

    setWeightBasis(value: 'equal' | 'custom') {
        this.dispatch(setWeightBasis(value));
    }

    #notionalAmount: ReadonlySignal<number | undefined> = computed(() => this.#state.value.notionalAmount);
    get notionalAmount() {
        return this.#notionalAmount;
    }

    setNotionalAmount(value: number | undefined) {
        this.dispatch(setNotionalAmount(value));
    }

    #notionalType: ReadonlySignal<'net' | 'gross'> = computed(() => this.#state.value.notionalType);
    get notionalType() {
        return this.#notionalType;
    }

    setNotionalType(value: 'net' | 'gross') {
        this.dispatch(setNotionalType(value));
    }

    #positionsAccount: ReadonlySignal<string | undefined> = computed(() => {
        const state = this.#state.value;
        // Only use positionsAccount for positionAdjustment basket type
        // Other basket types always use defaults.account for position loading
        if (state.type === 'positionAdjustment') {
            // If positionsAccount is explicitly set, use it
            if (state.positionsAccount) {
                return state.positionsAccount;
            }
            // Otherwise, calculate the default inline to avoid race conditions
            // where the auto-select effect hasn't fired yet
            const accounts = this.#accounts.value;
            if (accounts.length > 0) {
                return getPreferredAccountId(
                    this.currentUserModel.userId.value,
                    accounts,
                    BASKET_TRADING_PREFERRED_POSITION_ADJUSTMENT_ACCOUNT,
                );
            }
            return undefined;
        }
        return state.defaults.account;
    });
    get positionsAccount() {
        return this.#positionsAccount;
    }

    setPositionsAccount(value: string | undefined) {
        this.dispatch(setPositionsAccount(value));
    }

    #isLoading = signal(false);
    readonly isLoading: ReadonlySignal<boolean> = computed(() => this.#isLoading.value);

    readonly ordersLimitReached: ReadonlySignal<boolean> = computed(() => this.#state.value.ordersLimitReached);

    #canSubmit: ReadonlySignal<boolean> = computed(() => {
        const state = this.#state.value;
        const hasOrders = state.basket.length > 0;
        const notLimitReached = !state.ordersLimitReached;

        if (!this.#isReady.value || !hasOrders || !notLimitReached) {
            return false;
        }

        // Additional validation for notional basket type
        if (state.type === 'notional') {
            // Check notionalAmount is set and > 0
            if (!state.notionalAmount || state.notionalAmount <= 0) {
                return false;
            }

            // Check weights sum to 100%
            if (!isWeightSumValid(state.basket)) {
                return false;
            }
        }

        return true;
    });
    get canSubmit() {
        return this.#canSubmit;
    }

    #loadAccounts() {
        this.#accountService.loadAccounts();
    }

    #accounts: ReadonlySignal<ConcreteAccount[]> = computed(() => {
        return (this.#accountService.accounts || []).toSorted((a, b) => {
            return generateAccountNameToDisplay(a).localeCompare(generateAccountNameToDisplay(b));
        });
    });
    get accounts() {
        return this.#accounts;
    }

    getAccountName(accountId: string) {
        return this.#accounts.value.find((a) => a.id === accountId)?.name || '';
    }

    #basket: ReadonlySignal<BasketOrder[]> = computed(() => this.#state.value.basket);
    get basket() {
        return this.#basket;
    }

    #marketValue = computed(() => {
        const isPositionAdjustment = this.#state.value.type === 'positionAdjustment';
        return this.#state.value.basket.reduce((total, order) => {
            const price = order.lastPrice?.value ?? 0;
            const quantity = isPositionAdjustment ? (order.adjustmentQuantity ?? 0) : (order.quantity ?? 0);
            const multiplier = order.multiplier ?? 1;
            const side = order.side === 'buy' ? 1 : -1;
            return total + price * quantity * multiplier * side;
        }, 0);
    });
    get marketValue(): ReadonlySignal<number> {
        return this.#marketValue;
    }

    #totalCost = computed(() => {
        const isPositionAdjustment = this.#state.value.type === 'positionAdjustment';
        return this.#state.value.basket.reduce((total, order) => {
            const price = order.lastPrice?.value ?? 0;
            const quantity = isPositionAdjustment ? (order.adjustmentQuantity ?? 0) : (order.quantity ?? 0);
            const multiplier = order.multiplier ?? 1;
            return total + price * quantity * multiplier;
        }, 0);
    });
    get totalCost(): ReadonlySignal<number> {
        return this.#totalCost;
    }

    #longCost = computed(() => {
        const isPositionAdjustment = this.#state.value.type === 'positionAdjustment';
        return this.#state.value.basket.reduce((total, order) => {
            const price = order.lastPrice?.value ?? 0;
            const quantity = isPositionAdjustment ? (order.adjustmentQuantity ?? 0) : (order.quantity ?? 0);
            const multiplier = order.multiplier ?? 1;
            const value = order.side === 'buy' ? price * quantity * multiplier : 0;
            return total + value;
        }, 0);
    });
    get longCost(): ReadonlySignal<number> {
        return this.#longCost;
    }

    #sellCost = computed(() => {
        const isPositionAdjustment = this.#state.value.type === 'positionAdjustment';
        return this.#state.value.basket.reduce((total, order) => {
            const price = order.lastPrice?.value ?? 0;
            const quantity = isPositionAdjustment ? (order.adjustmentQuantity ?? 0) : (order.quantity ?? 0);
            const multiplier = order.multiplier ?? 1;
            const value = order.side === 'sell' ? price * quantity * multiplier : 0;
            return total + value;
        }, 0);
    });
    get sellCost(): ReadonlySignal<number> {
        return this.#sellCost;
    }

    #shortCost = computed(() => {
        const isPositionAdjustment = this.#state.value.type === 'positionAdjustment';
        return this.#state.value.basket.reduce((total, order) => {
            const price = order.lastPrice?.value ?? 0;
            const quantity = isPositionAdjustment ? (order.adjustmentQuantity ?? 0) : (order.quantity ?? 0);
            const multiplier = order.multiplier ?? 1;
            const value = order.side === 'sell-short' ? price * quantity * multiplier : 0;
            return total + value;
        }, 0);
    });
    get shortCost(): ReadonlySignal<number> {
        return this.#shortCost;
    }

    #recentlyAccessedSignal = signal<Array<Universe | IdeaRecord>>([]);

    async #updateRecentlyAccessed() {
        const items = this.suggestionsService.getItems({
            types: ['universe', 'watchlist', 'screener', 'scanner', 'if-then'],
            query: 'accessedAt',
            limit: 3,
        });

        // Filter to only the types we support
        const filtered = items.filter(
            (item): item is { type: 'universe' | 'watchlist' | 'screener' | 'scanner' | 'if-then'; id: string } =>
                item.type === 'universe' ||
                item.type === 'watchlist' ||
                item.type === 'screener' ||
                item.type === 'scanner' ||
                item.type === 'if-then',
        );

        // Fetch all records in parallel
        const recordPromises = filtered.map(async ({ type, id }) => {
            try {
                if (type === 'universe') {
                    return await this.queryClient.fetchQuery(getUniverseByIdQuery(id));
                } else {
                    return await this.queryClient.fetchQuery(getIdeaRecordByIdQuery(id));
                }
            } catch (error) {
                log.error('Failed to fetch recently accessed item', { type, id, error });
                return undefined;
            }
        });

        const records = await Promise.all(recordPromises);

        // Filter out failed fetches (undefined) and missing records (null)
        this.#recentlyAccessedSignal.value = records.filter(
            (record): record is Universe | IdeaRecord => record != null,
        );
    }

    get recentlyAccessed(): ReadonlySignal<Array<Universe | IdeaRecord>> {
        return this.#recentlyAccessedSignal;
    }

    resetBasket() {
        this.dispatch(resetBasket());

        // Unsubscribe all market data updates
        for (const instrumentString in this.#marketDataUpdates) {
            if (TRACE) {
                log.trace('Removing market data subscription', {
                    instrumentString,
                    cleanUp: this.#marketDataUpdates[instrumentString],
                });
            }

            if (this.#marketDataUpdates[instrumentString]) {
                this.#marketDataUpdates[instrumentString][Symbol.dispose]();
                delete this.#marketDataUpdates[instrumentString];
            }
        }

        // Unsubscribe all position info updates
        for (const instrumentString in this.#positionInfoUpdates.value) {
            if (TRACE) {
                log.trace('Removing position info subscription', {
                    instrumentString,
                });
            }

            if (this.#positionInfoUpdates.value[instrumentString]) {
                this.#positionInfoUpdates.value[instrumentString][Symbol.dispose]();
            }
        }
        this.#positionInfoUpdates.value = {};
    }

    async addOrder(order: BasketOrder) {
        this.dispatch(addOrder(order));

        const instrumentString = instrumentToString(order.instrument);

        if (!this.#marketDataUpdates[instrumentString]) {
            this.#fetchMarketDataForSymbol(order.instrument).then((subscription) => {
                if (subscription) {
                    this.#marketDataUpdates[instrumentString] = subscription;
                }
            });
        }

        if (!this.#positionInfoUpdates.value[instrumentString]) {
            void this.#createPositionSubscription(order.instrument, instrumentString);
        }
    }

    async addOrders(orders: BasketOrder[]) {
        if (TRACE) {
            log.trace('Dispatching batch orders to state', { count: orders.length });
        }
        this.dispatch(addOrdersBatch(orders));

        if (TRACE) {
            log.trace('Creating subscriptions for batch orders in background', { count: orders.length });
        }

        orders.map(async (order) => {
            const instrumentString = instrumentToString(order.instrument);

            if (!this.#marketDataUpdates[instrumentString]) {
                try {
                    const marketSubscription = await this.#fetchMarketDataForSymbol(order.instrument);
                    if (marketSubscription) {
                        this.#marketDataUpdates[instrumentString] = marketSubscription;
                    }
                } catch (error) {
                    log.error('Error creating market data subscription', { error, instrumentString });
                }
            }

            if (!this.#positionInfoUpdates.value[instrumentString]) {
                void this.#createPositionSubscription(order.instrument, instrumentString);
            }
        });
    }

    removeOrder(order: BasketOrder) {
        this.dispatch(removeOrder(order));

        const instrumentString = instrumentToString(order.instrument);

        if (TRACE) {
            log.trace('Removing market data subscription', {
                instrumentString,
            });
        }

        // Unsubscribe from market data updates
        if (this.#marketDataUpdates[instrumentString]) {
            this.#marketDataUpdates[instrumentString][Symbol.dispose]();
            delete this.#marketDataUpdates[instrumentString];
        }

        // Unsubscribe from position info updates
        if (this.#positionInfoUpdates.value[instrumentString]) {
            if (TRACE) {
                log.trace('Removing position info subscription', {
                    instrumentString,
                });
            }
            this.#positionInfoUpdates.value[instrumentString][Symbol.dispose]();
            const { [instrumentString]: _, ...rest } = this.#positionInfoUpdates.value;
            this.#positionInfoUpdates.value = rest;
        }
    }

    updateOrder(order: BasketOrder) {
        this.dispatch(updateOrder(order));
    }

    redistributeWeights() {
        this.dispatch(redistributeWeights());
    }

    prepareOrders(): SendOrderData[] {
        const basket = this.#state.value.basket;
        const basketType = this.#basketType.value;

        const orders: SendOrderData[] = [];
        let skippedZeroQuantity = 0;

        if (!basket.length) {
            log.debug('prepareOrders called with empty basket');
            return orders;
        }

        log.debug('Preparing orders from basket', {
            basketSize: basket.length,
            basketType,
        });

        for (const order of this.#state.value.basket) {
            const instrument =
                order.instrument.type === 'option'
                    ? {
                          ...order.instrument,
                          expiry:
                              typeof order.instrument.expiry === 'string'
                                  ? order.instrument.expiry
                                  : (order.instrument.expiry?.toString?.() ?? ''),
                      }
                    : order.instrument;

            const side = order.side as SendOrderData['side'];

            let quantity = (order.quantity ?? 0) * (order.multiplier ?? 1);

            if (basketType === 'positionAdjustment' && order.adjustment !== undefined) {
                quantity = order.adjustment === 0 ? 0 : Math.floor(Math.abs(order.quantity * order.adjustment) / 100);
            }

            // Skip orders with zero quantity
            if (quantity === 0) {
                skippedZeroQuantity++;
                if (TRACE) {
                    log.trace('Skipping order with zero quantity', {
                        instrument: order.instrument,
                        originalQuantity: order.quantity,
                        multiplier: order.multiplier,
                        adjustment: order.adjustment,
                    });
                }
                continue;
            }

            const data: SendOrderData = {
                account: order.account || '',
                instrument,
                quantity,
                side,
                exDestination: order.exDestination,
                route: order.route,
                type: order.type as SendOrderData['type'],
                duration: order.duration as SendOrderData['duration'],
            };

            orders.push(data);
        }

        log.debug('Order preparation complete', {
            basketSize: basket.length,
            preparedOrderCount: orders.length,
            skippedZeroQuantity,
            basketType,
        });

        return orders;
    }

    async sendOrders(): Promise<Order[] | undefined> {
        const basket = this.#state.value.basket;
        const accountId = this.#state.value.defaults.account;
        const basketType = this.#state.value.type;

        log.info('Starting basket order submission', {
            basketType,
            basketSize: basket.length,
            accountId,
        });

        if (TRACE) {
            log.trace('Basket order details', { basket });
        }

        const orders: SendOrderData[] = this.prepareOrders();

        if (!orders.length) {
            log.warn('No orders to send in the basket after preparation', {
                originalBasketSize: basket.length,
                basketType,
                accountId,
            });
            return;
        }

        log.info('Sending batch order request', {
            orderCount: orders.length,
            accountId,
            basketType,
            instruments: orders.map((o) => instrumentWithStringExpiryToString(o.instrument)),
        });

        if (TRACE) {
            log.trace('Order details being sent', { orders });
        }

        try {
            const createdOrders = await this.tradingSocketService.createBatchOrders({ orders });

            log.info('Basket order submission successful', {
                submittedCount: orders.length,
                createdCount: createdOrders.length,
                orderIds: createdOrders.map((o) => o.id),
                accountId,
                basketType,
            });

            return createdOrders;
        } catch (error) {
            // Don't reset basket on error - let the user fix/retry
            log.error('Failed to send orders', {
                error: error instanceof Error ? error.message : String(error),
                orderCount: orders.length,
                accountId,
                basketType,
            });
            throw error;
        }
    }

    #loadBasketSymbolsLabel: ReadonlySignal<string> = computed(
        () => this.#state.value.label ?? LOAD_BASKET_SYMBOL_LABEL,
    );
    get loadBasketSymbolsLabel(): ReadonlySignal<string> {
        return this.#loadBasketSymbolsLabel;
    }

    async #getWatchlist(watchlistId: string): Promise<string[]> {
        let symbols: string[] = [];

        try {
            const data = await this.queryClient.fetchQuery(getIdeaByIdQuery(watchlistId));
            symbols = (data?.plan as WatchlistPlan)?.includeList ?? [];
        } catch (error) {
            this.#enqueueSnackbar('widgets::basket-trading::failed-to-load-watchlist', { variant: 'error' });
            log.error('Failed to load watchlist:', { error, watchlistId });
        }

        return symbols;
    }

    async loadBasketFromWatchlist(watchlist: IdeaRecord, defaults: NewBasketOrderSchemaType): Promise<void> {
        this.#isLoading.value = true;
        this.resetBasket();
        this.dispatch(setBasketLabel(`Watchlist: ${watchlist.name}`));

        // Track access
        this.suggestionsService.addRecentlyAccessed({
            type: watchlist.planType,
            id: watchlist.id,
        });
        void this.#updateRecentlyAccessed();

        const watchlistSymbols = await this.#getWatchlist(watchlist.id);

        if (!watchlistSymbols.length) {
            this.#isLoading.value = false;
            this.dispatch(setBasketLabel(undefined));
            return;
        }

        const orders = watchlistSymbols.map((instrumentString) => ({
            ...defaults,
            instrument: parseInstrument(instrumentString),
            lastPrice: signal(undefined),
            bidPrice: signal(undefined),
            askPrice: signal(undefined),
            position: this.getPositionInfo(instrumentString),
        }));

        await this.addOrders(orders);
        this.#isLoading.value = false;
    }

    async #getUniverse(universeId: string): Promise<string[] | void> {
        using table = await this.tableModelProvider();
        table.init({
            queryOptions: {
                queryKey: ['table', 'universe', universeId],
                queryFn: async (_context, debugIdentifier?: string) => {
                    const slugMapService = await this.slugMapServiceProvider();
                    const columns = ensureColumns(slugMapService.slugMap.value, [], [Slugs.symbol]);
                    const stage = OutputStage.fromInputStage(SourceStage.fromTickerUniverseId(universeId), {}, columns);
                    return {
                        ...(await createTable({ leafStage: stage.build() }, undefined, debugIdentifier)),
                        fetchMode: 'snapshot',
                    };
                },
                staleTime: 0,
                gcTime: 0,
            },
            devtoolsName: 'BasketTrading/universe',
        });

        try {
            await table.waitForTableConnection();
        } catch (error) {
            this.#enqueueSnackbar('widgets::basket-trading::failed-to-connect', { variant: 'error' });
            log.error('Failed to connect to table', { error, universeId });
            return;
        }

        const snapshot = await table.snapshot({ firstRow: 0, windowSize: 1000000 });

        const symbolColumnId = table.columns?.find((column) => column.matchSlug === Slugs.symbol)?.id;

        if (!symbolColumnId) {
            return;
        }

        return snapshot?.data.map((row: Record<string, any>) => row[symbolColumnId] as string) || [];
    }

    async loadBasketFromUniverse(universe: Universe, defaults: NewBasketOrderSchemaType): Promise<void> {
        this.#isLoading.value = true;

        try {
            this.resetBasket();
            this.dispatch(setBasketLabel(`Universe: ${universe.name}`));

            // Track access
            this.suggestionsService.addRecentlyAccessed({
                type: 'universe',
                id: universe.id,
            });
            void this.#updateRecentlyAccessed();

            const symbols = (await this.#getUniverse(universe.id)) || [];

            if (!symbols.length) return;

            const orders = symbols.map((instrumentString) => ({
                ...defaults,
                instrument: parseInstrument(instrumentString),
                lastPrice: signal(undefined),
                bidPrice: signal(undefined),
                askPrice: signal(undefined),
                position: this.getPositionInfo(instrumentString),
            }));

            await this.addOrders(orders);
        } catch (error) {
            this.#enqueueSnackbar('widgets::basket-trading::failed-to-load-universe', { variant: 'error' });
            this.dispatch(setBasketLabel(undefined));
            log.error('Failed to load universe', { error, universe });
        } finally {
            this.#isLoading.value = false;
        }
    }

    async #getSearch(searchId: string): Promise<string[] | void> {
        const TABLE = 'table';
        const ROW_COUNT = 'row-count';
        const FILTERED = 'filtered';

        using table = await this.tableModelProvider();
        table.init({
            queryOptions: {
                queryKey: [TABLE, ROW_COUNT, FILTERED, searchId],
                queryFn: async (_context, debugIdentifier?: string) => {
                    const stage = IdeaStage.fromInputStage(SourceStage.allForKey('ticker'), searchId);
                    return {
                        ...(await createTable({ leafStage: stage.build() }, undefined, debugIdentifier)),
                        fetchMode: 'snapshot',
                    };
                },
                staleTime: 0,
                gcTime: 0,
            },
            devtoolsName: 'BasketTrading/filtered',
        });

        try {
            await table.waitForTableConnection();
        } catch (error) {
            this.#enqueueSnackbar('widgets::basket-trading::failed-to-connect', { variant: 'error' });
            log.error('Failed to connect to table', { error, searchId });
            return;
        }

        const snapshot = await table.snapshot({ firstRow: 0, windowSize: 1000000 });

        return snapshot?.data.map((row: { ticker: string }) => row['ticker'] as string) || [];
    }

    async loadBasketFromSearch(search: IdeaRecord, defaults: NewBasketOrderSchemaType): Promise<void> {
        this.#isLoading.value = true;

        try {
            this.resetBasket();
            this.dispatch(setBasketLabel(`Search: ${search.name}`));

            // Track access
            this.suggestionsService.addRecentlyAccessed({
                type: search.planType,
                id: search.id,
            });
            void this.#updateRecentlyAccessed();

            const symbols = (await this.#getSearch(search.id)) || [];

            if (!symbols.length) return;

            const orders = symbols.map((instrumentString) => ({
                ...defaults,
                instrument: parseInstrument(instrumentString),
                lastPrice: signal(undefined),
                bidPrice: signal(undefined),
                askPrice: signal(undefined),
                position: this.getPositionInfo(instrumentString),
            }));

            await this.addOrders(orders);
        } catch (error) {
            this.#enqueueSnackbar('widgets::basket-trading::failed-to-load-search', { variant: 'error' });
            this.dispatch(setBasketLabel(undefined));
            log.error('Failed to load search', { error, search });
        } finally {
            this.#isLoading.value = false;
        }
    }

    async #getPositions(accountId?: string): Promise<void | PositionInfo[]> {
        try {
            using table = await this.tableModelProvider();
            table.init({
                queryOptions: {
                    queryKey: ['positions-widget-table', accountId ?? 'all'],
                    queryFn: async (_context, debugIdentifier?: string) => {
                        const slugMapService = await this.slugMapServiceProvider();

                        let tableBuilder: SourceStage | FilterStage = SourceStage.allForKey('Position');

                        let columns = ensureColumns(
                            slugMapService.slugMap.value,
                            [],
                            [Slugs.positionSymbol, Slugs.positionOpenShares],
                        );

                        let formula = '';
                        const imports: IndicatorImportRef[] = [];

                        if (accountId) {
                            const accountIdAlias = 'ACCOUNT_ID';
                            const accountIdIndicator = slugMapService.slugMap.value['account::accountId'];

                            imports.push({
                                id: accountIdIndicator.id,
                                alias: 'ACCOUNT_ID',
                            });

                            formula = `(${accountIdAlias} == "${accountId}")`;

                            columns = ensureColumnBySlugWithName(slugMapService.slugMap.value, columns, {
                                name: accountIdAlias,
                                slug: 'account::accountId',
                            });
                        }

                        if (formula) {
                            tableBuilder = FilterStage.fromInputStage(tableBuilder, {
                                type: 'filter',
                                formula,
                                imports,
                            });
                        }

                        return {
                            ...(await createTable(
                                {
                                    leafStage: OutputStage.fromInputStage(tableBuilder, {}, columns).build(),
                                },
                                undefined,
                                debugIdentifier,
                            )),
                            fetchMode: 'snapshot',
                        };
                    },
                    staleTime: 0,
                    gcTime: 0,
                },
                devtoolsName: 'BasketTrading/positions',
            });

            try {
                await table.waitForTableConnection();
            } catch (error) {
                this.#enqueueSnackbar('widgets::basket-trading::failed-to-connect', { variant: 'error' });
                log.error('Failed to connect to table', { error });
                return;
            }

            const snapshot = await table.snapshot({ firstRow: 0, windowSize: 1000000 });

            const symbolColumnId = table.columns?.find((column) => column.matchSlug === Slugs.positionSymbol)?.id;
            const sharesColumnId = table.columns?.find((column) => column.matchSlug === Slugs.positionOpenShares)?.id;

            if (!symbolColumnId || !sharesColumnId) {
                return;
            }

            return (
                snapshot?.data.reduce((acc: { symbol: string; quantity: number }[], row: Record<string, any>) => {
                    const quantity = Number(row[sharesColumnId] ?? 0);
                    if (quantity !== 0) {
                        acc.push({
                            symbol: row[symbolColumnId],
                            quantity,
                        });
                    }
                    return acc;
                }, []) || []
            );
        } catch (error) {
            log.error('Failed to load positions', { error });
        }
    }

    getPositionInfo(symbol: string | Instrument): ReadonlySignal<number | undefined> {
        return computed(() => {
            const symbolString = typeof symbol === 'string' ? symbol : instrumentToString(symbol);

            const positionSubscription = this.#positionInfoUpdates.value[symbolString];

            if (!positionSubscription) {
                if (TRACE) {
                    log.trace('Position subscription not ready yet', {
                        symbol: symbolString,
                        availableSubscriptions: Object.keys(this.#positionInfoUpdates.value),
                    });
                }
                return undefined;
            }

            const start = positionSubscription.positionInfo.value.positionSOD;
            const bought = positionSubscription.positionInfo.value.positionBoughtShares;
            const sold = positionSubscription.positionInfo.value.positionSoldShares;

            return start === undefined || bought === undefined || sold === undefined
                ? undefined
                : start + bought - sold;
        });
    }

    async loadBasketFromPositions(defaults: NewBasketOrderSchemaType, usePositionQuantity = false): Promise<void> {
        this.#isLoading.value = true;

        try {
            this.resetBasket();
            this.dispatch(setBasketLabel(`Positions`));

            const positions = (await this.#getPositions(this.#positionsAccount.value)) || [];

            if (!positions.length) {
                this.dispatch(setBasketLabel(undefined));
                return;
            }

            const orders = positions.map((position) => ({
                ...defaults,
                instrument: parseInstrument(position.symbol),
                quantity: usePositionQuantity ? position.quantity : defaults.quantity,
                lastPrice: signal(undefined),
                bidPrice: signal(undefined),
                askPrice: signal(undefined),
                position: this.getPositionInfo(position.symbol),
            }));

            await this.addOrders(orders);
        } finally {
            this.#isLoading.value = false;
        }
    }

    async loadBasketFromPositionsIntersectingWatchlist(
        watchlist: IdeaRecord,
        defaults: NewBasketOrderSchemaType,
        usePositionQuantity = false,
    ): Promise<void> {
        this.#isLoading.value = true;

        try {
            this.resetBasket();
            this.dispatch(setBasketLabel(`Watchlist Positions: ${watchlist.name}`));

            // Track access
            this.suggestionsService.addRecentlyAccessed({
                type: watchlist.planType,
                id: watchlist.id,
            });
            void this.#updateRecentlyAccessed();

            const symbols = await this.#getWatchlist(watchlist.id);
            if (!symbols.length) {
                this.dispatch(setBasketLabel(undefined));
                return;
            }

            const positions = (await this.#getPositions(this.#positionsAccount.value)) || [];

            if (!positions.length) {
                this.dispatch(setBasketLabel(undefined));
                return;
            }

            const orders: BasketOrder[] = [];
            for (const position of positions) {
                if (!symbols.includes(position.symbol)) {
                    continue;
                }

                orders.push({
                    ...defaults,
                    instrument: parseInstrument(position.symbol),
                    quantity: usePositionQuantity ? position.quantity : defaults.quantity,
                    lastPrice: signal(undefined),
                    bidPrice: signal(undefined),
                    askPrice: signal(undefined),
                } as BasketOrder);
            }

            await this.addOrders(orders);
        } finally {
            this.#isLoading.value = false;
        }
    }

    async loadBasketFromPositionsIntersectingUniverse(
        universe: Universe,
        defaults: NewBasketOrderSchemaType,
        usePositionQuantity = false,
    ): Promise<void> {
        this.#isLoading.value = true;

        try {
            this.resetBasket();
            this.dispatch(setBasketLabel(`Universe Positions: ${universe.name}`));

            // Track access
            this.suggestionsService.addRecentlyAccessed({
                type: 'universe',
                id: universe.id,
            });
            void this.#updateRecentlyAccessed();

            const symbols = (await this.#getUniverse(universe.id)) || [];

            if (!symbols.length) {
                this.dispatch(setBasketLabel(undefined));
                return;
            }

            const positions = (await this.#getPositions(this.#positionsAccount.value)) || [];

            if (!positions.length) {
                this.dispatch(setBasketLabel(undefined));
                return;
            }

            const orders: BasketOrder[] = [];
            for (const position of positions) {
                if (!symbols.includes(position.symbol)) {
                    continue;
                }

                orders.push({
                    ...defaults,
                    instrument: parseInstrument(position.symbol),
                    quantity: usePositionQuantity ? position.quantity : defaults.quantity,
                    lastPrice: signal(undefined),
                    bidPrice: signal(undefined),
                    askPrice: signal(undefined),
                } as BasketOrder);
            }

            await this.addOrders(orders);
        } finally {
            this.#isLoading.value = false;
        }
    }

    async loadBasketFromPositionsIntersectingSearch(
        search: IdeaRecord,
        defaults: NewBasketOrderSchemaType,
        usePositionQuantity = false,
    ): Promise<void> {
        this.#isLoading.value = true;

        try {
            this.resetBasket();
            this.dispatch(setBasketLabel(`Search Positions: ${search.name}`));

            // Track access
            this.suggestionsService.addRecentlyAccessed({
                type: search.planType,
                id: search.id,
            });
            void this.#updateRecentlyAccessed();

            const symbols = (await this.#getSearch(search.id)) || [];

            if (!symbols.length) {
                this.dispatch(setBasketLabel(undefined));
                return;
            }

            const positions = (await this.#getPositions(this.#positionsAccount.value)) || [];

            if (!positions.length) {
                this.dispatch(setBasketLabel(undefined));
                return;
            }

            const orders: BasketOrder[] = [];
            for (const position of positions) {
                if (!symbols.includes(position.symbol)) {
                    continue;
                }

                orders.push({
                    ...defaults,
                    instrument: parseInstrument(position.symbol),
                    quantity: usePositionQuantity ? position.quantity : defaults.quantity,
                    lastPrice: signal(undefined),
                    bidPrice: signal(undefined),
                    askPrice: signal(undefined),
                } as BasketOrder);
            }

            await this.addOrders(orders);
        } finally {
            this.#isLoading.value = false;
        }
    }

    async loadBasketFromPositionsIntersectingInstruments(
        instruments: Instrument[],
        defaults: NewBasketOrderSchemaType,
        usePositionQuantity = false,
    ): Promise<void> {
        this.#isLoading.value = true;

        try {
            this.resetBasket();

            const symbols = instruments.map((inst) => instrumentToString(inst));

            if (!symbols.length) return;

            const positions = (await this.#getPositions(this.#positionsAccount.value)) || [];

            if (!positions.length) return;

            const orders: BasketOrder[] = [];
            for (const position of positions) {
                if (!symbols.includes(position.symbol)) {
                    continue;
                }

                orders.push({
                    ...defaults,
                    instrument: parseInstrument(position.symbol),
                    quantity: usePositionQuantity ? position.quantity : defaults.quantity,
                    lastPrice: signal(undefined),
                    bidPrice: signal(undefined),
                    askPrice: signal(undefined),
                    position: this.getPositionInfo(position.symbol),
                } as BasketOrder);
            }

            await this.addOrders(orders);
        } catch (error) {
            this.#enqueueSnackbar('widgets::basket-trading::failed-to-load-positions', { variant: 'error' });
            log.error('Failed to load positions', { error });
        } finally {
            this.#isLoading.value = false;
        }
    }

    #findOrderByInstrument(instrument: Instrument) {
        return this.#state.value.basket.find(
            (o) => instrumentToString(o.instrument) === instrumentToString(instrument),
        );
    }

    async #fetchMarketDataForSymbol(instrument: Instrument): Promise<MarketDataSubscription | undefined> {
        // Use the appropriate MarketDataGroupService based on instrument type
        const marketDataGroup =
            instrument.type === 'option' ? this.#optionsMarketDataGroup : this.#equityMarketDataGroup;
        const marketSubscription = marketDataGroup.subscribe(instrument);

        if (!marketSubscription) {
            // User lacks permission to access market data
            return undefined;
        }

        if (TRACE) {
            log.trace('Connected to market data updates', {
                instrument: instrumentToString(instrument),
                instrumentType: instrument.type,
            });
        }

        this.disposableStack.defer(
            effect(() => {
                const marketDataValue = marketSubscription.marketData.value;

                if (!marketDataValue) {
                    return;
                }

                const order = this.#findOrderByInstrument(instrument);
                if (!order) {
                    return;
                }

                batch(() => {
                    order.lastPrice.value = Number.isNaN(marketDataValue.last) ? undefined : marketDataValue.last;
                    order.bidPrice.value = Number.isNaN(marketDataValue.bid) ? undefined : marketDataValue.bid;
                    order.askPrice.value = Number.isNaN(marketDataValue.ask) ? undefined : marketDataValue.ask;
                });

                if (TRACE) {
                    log.trace('Received market data updates', {
                        instrument: instrumentToString(instrument),
                        marketDataValue,
                    });
                }
            }),
        );

        return marketSubscription;
    }

    async #fetchPositionInfoForSymbol(instrument: Instrument): Promise<PositionInfoSubscription | undefined> {
        if (!this.#positionInfoGroup) {
            log.warn('No PositionInfoGroupService available, skipping position subscription', {
                instrument: instrumentToString(instrument),
                account: this.#state.value.defaults.account,
            });
            return undefined;
        }

        const positionSubscription = this.#positionInfoGroup.subscribe(instrument);

        if (TRACE) {
            log.trace('Connected to position info updates', {
                instrument: instrumentToString(instrument),
            });
        }

        return positionSubscription;
    }

    async #createPositionSubscription(instrument: Instrument, instrumentString: string): Promise<void> {
        try {
            if (this.#positionInfoGroupReady) {
                await this.#positionInfoGroupReady;
            }

            log.debug('Creating position subscription for order', {
                instrumentString,
                hasPositionInfoGroup: !!this.#positionInfoGroup,
            });

            const subscription = await this.#fetchPositionInfoForSymbol(instrument);
            if (subscription) {
                batch(() => {
                    this.#positionInfoUpdates.value = {
                        ...this.#positionInfoUpdates.value,
                        [instrumentString]: subscription,
                    };
                });
            } else {
                log.warn('Failed to create position subscription', { instrumentString });
            }
        } catch (error) {
            log.error('Error creating position subscription', { error, instrumentString });
        }
    }

    // --- Save/Load methods ---

    #markClean(): void {
        this.#lastSavedStateJson.value = serializableBasketSnapshot(this.#state.value);
        this.#lastSavedName.value = this.#basketName.value;
    }

    async #saveBasket(): Promise<void> {
        const name = this.#basketName.value || 'Untitled Basket';
        const loadedId = this.#loadedBasketId.value;

        if (!loadedId) {
            await this.#saveBasketAs(name);
            return;
        }

        try {
            log.info('Saving basket spec', { specId: loadedId, name });
            const spec = basketStateToSpec(this.#state.value, name);
            const updated = await updateBasketSpec(loadedId, spec);
            this.#loadedBasketId.value = updated.id;
            this.#basketName.value = name;
            this.#markClean();
            invalidateBasketSpecsQueries(this.queryClient);
            this.#enqueueSnackbar('widgets::basket-trading::save-success', { variant: 'success' });
            log.info('Basket spec updated', { specId: loadedId, name });
        } catch (error) {
            log.error('Failed to update basket spec', { error, specId: loadedId });
            this.#enqueueSnackbar('widgets::basket-trading::save-error', { variant: 'error' });
        }
    }

    async #saveBasketAs(name: string): Promise<void> {
        try {
            log.info('Creating basket spec', { name });
            this.#basketName.value = name;
            const spec = basketStateToSpec(this.#state.value, name);
            const created = await createBasketSpec(spec);
            this.#loadedBasketId.value = created.id;
            this.#markClean();
            invalidateBasketSpecsQueries(this.queryClient);
            this.#enqueueSnackbar('widgets::basket-trading::save-success', { variant: 'success' });
            log.info('Basket spec created', { specId: created.id, name });
        } catch (error) {
            log.error('Failed to create basket spec', { error });
            this.#enqueueSnackbar('widgets::basket-trading::save-error', { variant: 'error' });
        }
    }

    #createNewBasket(): void {
        this.dispatch(resetBasket());
        this.#loadedBasketId.value = null;
        this.#basketName.value = '';
        this.#lastSavedStateJson.value = serializableBasketSnapshot(this.#state.value);
        this.#lastSavedName.value = this.#basketName.value;
    }

    async #loadBasketById(specId: string): Promise<void> {
        try {
            const spec = await getBasketSpecById(specId);
            if (!spec) {
                log.warn('Basket spec not found', { specId });
                return;
            }

            this.#loadBasketSpec(spec);
        } catch (error) {
            log.error('Failed to load basket spec', { error, specId });
        }
    }

    #loadBasketSpec(spec: BasketSpec): void {
        // Reset current basket
        this.dispatch(resetBasket());

        // Extract defaults from the flat spec and sanitize — replace null values with undefined for form compatibility
        const defaults = {
            account: spec.account,
            side: spec.side,
            route: spec.route,
            exDestination: spec.exDestination,
            type: spec.type,
            quantity: spec.quantity,
            duration: spec.duration,
            direction: spec.direction,
            multiplier: spec.multiplier,
            adjustment: spec.adjustment,
        };
        const sanitizedDefaults = Object.fromEntries(
            Object.entries(defaults).map(([k, v]) => [k, v === null ? undefined : v]),
        ) as NewBasketOrderSchemaType;

        // Set defaults first (before type change, to avoid the account-change basket clear)
        this.dispatch(setDefaults(sanitizedDefaults));
        this.dispatch(setBasketType(spec.basketType));
        this.dispatch(setWeightBasis(spec.weightBasis));
        if (spec.notionalAmount != null) {
            this.dispatch(setNotionalAmount(spec.notionalAmount));
        }
        this.dispatch(setNotionalType(spec.notionalType));
        if (spec.positionsAccount) {
            this.dispatch(setPositionsAccount(spec.positionsAccount));
        }

        // Load orders — each order needs fresh market data signals
        const ordersWithSignals = spec.orders.map((specOrder) => ({
            ...specOrder,
            account: specOrder.account ?? spec.account,
            quantity: specOrder.quantity ?? spec.quantity,
            side: specOrder.side ?? spec.side,
            route: specOrder.route ?? spec.route,
            exDestination: specOrder.exDestination ?? spec.exDestination,
            type: specOrder.type ?? spec.type,
            duration: specOrder.duration ?? spec.duration,
            lastPrice: signal<number | undefined>(undefined),
            bidPrice: signal<number | undefined>(undefined),
            askPrice: signal<number | undefined>(undefined),
            position: computed(() => undefined as number | undefined),
        }));
        this.dispatch(addOrdersBatch(ordersWithSignals));

        // Subscribe to market data for loaded orders
        for (const order of ordersWithSignals) {
            const instrumentString = instrumentToString(order.instrument);
            if (!this.#marketDataUpdates[instrumentString]) {
                this.#fetchMarketDataForSymbol(order.instrument).then((subscription) => {
                    if (subscription) {
                        this.#marketDataUpdates[instrumentString] = subscription;
                    }
                });
            }
            if (!this.#positionInfoUpdates.value[instrumentString]) {
                void this.#createPositionSubscription(order.instrument, instrumentString);
            }
        }

        // Update save/load state
        this.#loadedBasketId.value = spec.id;
        this.#basketName.value = spec.name;
        this.#lastSavedStateJson.value = serializableBasketSnapshot(this.#state.value);
        this.#lastSavedName.value = this.#basketName.value;
        log.info('Basket spec loaded', { specId: spec.id, name: spec.name, orderCount: spec.orders.length });
    }
}
